'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { getCreationDetail } from '@/app/apis';
import type { CreationWithTask, ServiceError } from '@/app/types';
import { mergeCreationAndTask } from '@/app/utils';
import { useFetch } from './use-fetch';

type UseCreationDetailNavigationOptions = {
  initialId: string;
};

export function useCreationDetailNavigation({ initialId }: UseCreationDetailNavigationOptions) {
  const [activeCreationId, setActiveCreationId] = useState(initialId);
  const [currentCreation, setCurrentCreation] = useState<CreationWithTask | undefined>(undefined);
  const [hasRequested, setHasRequested] = useState(false);
  const activeIdRef = useRef(activeCreationId);

  useEffect(() => {
    setActiveCreationId(initialId);
  }, [initialId]);

  useEffect(() => {
    activeIdRef.current = activeCreationId;
  }, [activeCreationId]);

  const {
    error,
    loading,
    run: fetchDetail,
  } = useFetch(
    (id: string) =>
      getCreationDetail({
        path: { id },
      }),
    {
      manual: true,
      onSuccess: ({ data }) => {
        if (!data?.creation || !data?.task) return;
        if (data.creation.id !== activeIdRef.current) return;
        setCurrentCreation(
          mergeCreationAndTask({
            creation: data.creation,
            task: data.task,
            creationId: data.creation.id,
            extendSourceCreations: data.extend_source_creations,
          }),
        );
      },
    },
  );

  useEffect(() => {
    if (!activeCreationId) return;
    setHasRequested(true);
    fetchDetail(activeCreationId);
  }, [activeCreationId, fetchDetail]);

  const isInitialLoading = useMemo(
    () => !currentCreation && (!hasRequested || loading),
    [currentCreation, hasRequested, loading],
  );
  const isSwitchLoading = useMemo(
    () => !!currentCreation && loading && currentCreation.id !== activeCreationId,
    [currentCreation, loading, activeCreationId],
  );

  return {
    activeCreationId,
    currentCreation,
    setCurrentCreation,
    loading,
    isInitialLoading,
    isSwitchLoading,
    error: error as unknown as ServiceError | undefined,
    setActiveCreationId,
  };
}
